import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isNil } from 'lodash'
import classNames from 'classnames'
import { IconBrandTeams } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import {
  getTeamsOAuthUrl,
  fetchTeamsStatus,
  disconnectTeams,
} from '@/reducers/organizations/organization.actions'

/**
 * Modal content for self-serve Microsoft Teams notification setup (org admins).
 * Shows a Connect prompt when the org has no active Teams credential, or a
 * connected status with Reconnect + Disconnect when it does. Connecting uses
 * Microsoft's admin-consent flow — a tenant admin approves the Balto app once,
 * then managers toggle Teams delivery in their own notification preferences.
 *
 * @param {object} props
 * @param {number} props.organizationId - org to manage; the backend re-checks
 *   the caller is authorized for it (authorize_for_org).
 * @param {() => void} props.onClose - closes the modal (injected by ButtonAndFormModal)
 */
export const TeamsNotificationsForm = ({ organizationId, onClose }) => {
  const dispatch = useDispatch()
  // null while the initial status check is in flight.
  const [status, setStatus] = useState(null)
  const [busy, setBusy] = useState(false)

  const loadStatus = async () => {
    const result = await dispatch(fetchTeamsStatus({ organizationId }))
    setStatus(result || { connected: false })
  }

  useEffect(() => {
    loadStatus()
  }, [organizationId, dispatch])

  const handleConnect = async () => {
    setBusy(true)
    // Full-page redirect to Microsoft's admin-consent screen; the callback
    // returns the browser here with ?teams=connected|error. Safe relative
    // paths only.
    const url = await dispatch(
      getTeamsOAuthUrl({ organizationId, returnTo: window.location.pathname })
    )
    if (url) {
      window.location.assign(url)
      return
    }
    setBusy(false)
  }

  const handleDisconnect = async () => {
    setBusy(true)
    const disconnected = await dispatch(disconnectTeams({ organizationId }))
    if (disconnected) {
      setStatus({ connected: false })
    }
    setBusy(false)
  }

  const loading = isNil(status)
  const connected = !loading && status.connected

  return (
    <div className="teams-notifications-form">
      {loading && <p className="small-text">Checking Teams connection…</p>}

      {connected && (
        <div className="flex-align-center small-gap" data-testid="teams-connected-status">
          <Pill small success>
            Teams connected
          </Pill>
          {status.last_updated && (
            <span className="small-text">
              Connected{' '}
              {new Date(status.last_updated).toLocaleDateString('en-US', { timeZone: 'UTC' })}
            </span>
          )}
        </div>
      )}

      {connected && (
        <p className="small-text">
          Balto DMs your managers directly in Teams. Reconnect to re-approve for a different tenant,
          or disconnect to stop Teams delivery for this organization.
        </p>
      )}

      {!loading && !connected && (
        <p className="small-text">
          Connect Microsoft Teams so Balto can DM managers their notifications. A Teams
          administrator approves the Balto app for your tenant, then you’ll be brought right back
          here.
        </p>
      )}

      <div className="form-actions is-modal">
        {connected && (
          <Button
            type="button"
            critical
            onClick={handleDisconnect}
            disabled={busy}
            data-testid="disconnect-teams-button"
          >
            Disconnect
          </Button>
        )}
        {/* ml-auto on Cancel pushes it right, leaving Disconnect at the far left */}
        <Button type="button" className={classNames({ 'ml-auto': connected })} onClick={onClose}>
          Cancel
        </Button>
        {!loading && (
          <Button
            type="button"
            brand
            onClick={handleConnect}
            disabled={busy}
            data-testid="connect-teams-button"
          >
            <IconBrandTeams />
            {connected ? 'Reconnect' : 'Connect Teams'}
          </Button>
        )}
      </div>
    </div>
  )
}
